import type { FirebaseApp } from 'firebase/app';

/**
 * Lazy Firebase initialisation.
 *
 * Everything here is behind a dynamic import on purpose. The landing page — the
 * one with a Lighthouse ≥90 mobile budget that opens from a WhatsApp link — needs
 * no Firebase at all, so none of the SDK belongs in its chunk. The first code to
 * actually need Firebase (the /join form, /admin sign-in) pays for it.
 */

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const USE_EMULATORS = import.meta.env.VITE_USE_EMULATORS === 'true';

let appPromise: Promise<FirebaseApp> | null = null;

async function getApp(): Promise<FirebaseApp> {
  if (!appPromise) {
    appPromise = (async () => {
      const { initializeApp, getApps, getApp: existing } = await import('firebase/app');
      return getApps().length > 0 ? existing() : initializeApp(config);
    })();
  }
  return appPromise;
}

let appCheckPromise: Promise<void> | null = null;

/**
 * Initialise App Check — for callables only, and only on demand.
 *
 * This used to sit inside getApp(), so *every* Firebase consumer paid for it:
 * the app-check chunk plus a reCAPTCHA Enterprise script fetched from Google,
 * both blocking. /admin felt slow largely because of it, and /admin does not
 * need it — App Check enforcement is configured on none of the Google services
 * (Firestore included). The only thing that enforces it is our own callables,
 * via `enforceAppCheck` in each onCall and ENFORCE_APP_CHECK in functions/.env.
 *
 * So the cost now falls where the requirement is. Direct Firestore reads (the
 * /admin panels, /history) and Auth skip it entirely; anything going through
 * callFunction awaits it first, because a callable without a token is a 401.
 *
 * ⚠️ If App Check enforcement is ever switched on for Firestore in the console,
 * this has to be awaited before getFirestoreModule too, or every direct read
 * starts failing.
 */
async function ensureAppCheck(): Promise<void> {
  const siteKey = import.meta.env.VITE_APPCHECK_SITE_KEY;
  if (!siteKey || USE_EMULATORS) return;

  if (!appCheckPromise) {
    appCheckPromise = (async () => {
      const app = await getApp();
      const { initializeAppCheck, ReCaptchaEnterpriseProvider } = await import(
        'firebase/app-check'
      );
      initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(siteKey),
        isTokenAutoRefreshEnabled: true,
      });
    })();
  }
  return appCheckPromise;
}

/** Callable-function invoker. Returns the function's payload. */
export async function callFunction<TRequest, TResponse>(
  name: string,
  data: TRequest,
): Promise<TResponse> {
  // Callables are the one thing that enforces App Check, so they are the one
  // thing that waits for it.
  const [app] = await Promise.all([getApp(), ensureAppCheck()]);
  const { getFunctions, httpsCallable, connectFunctionsEmulator } = await import(
    'firebase/functions'
  );

  const functions = getFunctions(app, 'us-central1');
  if (USE_EMULATORS) {
    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  }

  const fn = httpsCallable<TRequest, TResponse>(functions, name);
  const result = await fn(data);
  return result.data;
}

/** Auth + Firestore, for /admin only. Both lazy for the same reason. */
export async function getAuthModule() {
  const app = await getApp();
  const mod = await import('firebase/auth');
  const auth = mod.getAuth(app);
  if (USE_EMULATORS) {
    mod.connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  }
  return { auth, ...mod };
}

export async function getFirestoreModule() {
  const app = await getApp();
  const mod = await import('firebase/firestore');
  const dbInstance = mod.getFirestore(app);
  if (USE_EMULATORS) {
    mod.connectFirestoreEmulator(dbInstance, '127.0.0.1', 8080);
  }
  return { db: dbInstance, ...mod };
}

/**
 * Human-readable message from a callable error.
 * Firebase wraps HttpsError messages, and the raw shape is unhelpful in a form.
 */
export function callableErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = String((err as { message: unknown }).message);
    // Case-insensitive: the SDK reports an unreachable backend as "internal"
    // lowercase, which a startsWith('INTERNAL') check waves through — so a
    // dead emulator rendered the literal word "internal" in the error box.
    if (message && !message.toLowerCase().startsWith('internal')) return message;
  }
  return fallback;
}
